// src/utils/experience.js
// Single source of truth for experience calculation based on work history

/**
 * Calculate years of experience from the oldest job start date
 * @param {Array} experiences - Array of experience objects with startDate property
 * @returns {Object} { years, months, totalMonths, displayText, fullDisplayText, startDate }
 */
export const calculateYearsOfExperience = (experiences = []) => {
  if (!experiences || experiences.length === 0) {
    return {
      years: 0,
      months: 0,
      totalMonths: 0,
      displayText: '0+',
      fullDisplayText: '0 Months',
      startDate: null
    };
  }

  // Find the oldest (earliest) start date from all experiences, skipping missing/invalid dates
  const oldestStartDate = experiences.reduce((oldest, exp) => {
    const expStart = new Date(exp.startDate);
    if (!exp.startDate || isNaN(expStart)) return oldest;
    return !oldest || expStart < oldest ? expStart : oldest;
  }, null);

  if (!oldestStartDate) {
    return calculateYearsOfExperience([]);
  }

  // Calendar-month difference, same method as the per-job durations in Timeline.jsx
  const today = new Date();
  const totalMonths = Math.max(
    0,
    (today.getFullYear() - oldestStartDate.getFullYear()) * 12 + (today.getMonth() - oldestStartDate.getMonth())
  );
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  return {
    years,
    months,
    totalMonths,
    displayText: `${years}+`,
    fullDisplayText: years > 0
      ? `${years} Year${years !== 1 ? 's' : ''}${months > 0 ? ` ${months} Month${months !== 1 ? 's' : ''}` : ''}`
      : `${months} Month${months !== 1 ? 's' : ''}`,
    startDate: oldestStartDate
  };
};

/**
 * Get years as a simple number
 * @param {Array} experiences - Array of experience objects
 * @returns {number}
 */
export const getYearsOfExperience = (experiences = []) => {
  return calculateYearsOfExperience(experiences).years;
};
